/**
 * Hook para estado e solicitação de permissões do app (câmera, microfone, notificações).
 * Usado na tela de Permissões do app no perfil e no onboarding.
 *
 * Câmera/microfone passam pelo mecanismo unificado de priming
 * (`useDevicePermissionGate` → `useDevicePermissionRequest` + `PermissionPrimingSheet`):
 * o primeiro uso mostra o racional pt-BR antes do prompt do SO, contexto já concedido
 * não reabre o sheet e o estado negado orienta às Configurações. O consumidor apenas
 * renderiza `{permissionSheet}`. Notificações seguem o fluxo próprio (`Notification`).
 */

import { useCallback, useEffect, useState } from 'react'
import {
  queryDevicePermission,
  type DevicePermissionState,
} from '@/shared/lib/permissions/useDevicePermissionRequest'
import { useDevicePermissionGate } from '@/shared/lib/permissions/useDevicePermissionGate'

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

/** Racionais pt-BR exibidos no sheet de priming a partir do card/onboarding. */
const CAMERA_REASON = 'Escanear QR Code e códigos de barras.'
const MICROPHONE_REASON = 'Ditado por voz em campos clínicos (resumos, queixa, etc.).'

function queryNotifications(): PermissionState {
  if (typeof Notification === 'undefined') return 'unsupported'
  const p = Notification.permission
  return (p === 'default' ? 'prompt' : p) as PermissionState
}

export function useAppPermissions() {
  const { requestDeviceAccess, permissionSheet } = useDevicePermissionGate()
  const [camera, setCamera] = useState<PermissionState>('prompt')
  const [microphone, setMicrophone] = useState<PermissionState>('prompt')
  const [notifications, setNotifications] = useState<PermissionState>(queryNotifications)
  const [requesting, setRequesting] = useState<'camera' | 'microphone' | null>(null)

  const refresh = useCallback(() => {
    void queryDevicePermission('camera').then((state: DevicePermissionState) => setCamera(state))
    void queryDevicePermission('microphone').then((state: DevicePermissionState) =>
      setMicrophone(state),
    )
    setNotifications(queryNotifications())
  }, [])

  useEffect(() => {
    void queryDevicePermission('camera').then((state: DevicePermissionState) => setCamera(state))
    void queryDevicePermission('microphone').then((state: DevicePermissionState) =>
      setMicrophone(state),
    )
  }, [])

  const requestCamera = useCallback(async () => {
    setRequesting('camera')
    try {
      const outcome = await requestDeviceAccess('camera', { reason: CAMERA_REASON })
      await queryDevicePermission('camera').then((state) => setCamera(state))
      return outcome === 'granted'
    } finally {
      setRequesting(null)
    }
  }, [requestDeviceAccess])

  const requestMicrophone = useCallback(async () => {
    setRequesting('microphone')
    try {
      const outcome = await requestDeviceAccess('microphone', { reason: MICROPHONE_REASON })
      await queryDevicePermission('microphone').then((state) => setMicrophone(state))
      return outcome === 'granted'
    } finally {
      setRequesting(null)
    }
  }, [requestDeviceAccess])

  const hasMediaCapture = typeof navigator?.mediaDevices?.getUserMedia === 'function'

  return {
    camera,
    microphone,
    notifications,
    requesting,
    refresh,
    requestCamera,
    requestMicrophone,
    hasCamera: hasMediaCapture,
    hasMicrophone: hasMediaCapture,
    hasNotifications: typeof Notification !== 'undefined',
    /** Renderize este elemento para exibir o sheet de priming/bloqueio. */
    permissionSheet,
  }
}
